import { ActionIcon, TextInput } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Descendant } from 'slate';

import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { StepIndicator } from 'src/Components/Instructions/StepIndicator';
import { RichTextEditor } from 'src/Components/Shared/RichTextEditor';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { UploadableFile, useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';

import FormAttachmentViewer from '../../../FormAttachmentViewer/FormAttachmentViewer.component';

import styles from './InstructionFormSection.module.scss';
import { Props } from './InstructionFormSection.types';

const InstructionFormSection = ({ form, index, removeSection }: Props) => {
  const { t } = useTranslation();

  const handleEditorChange = (value: Descendant[]) => {
    form.setFieldValue(`sections.${index}.content`, value);
  };

  const titleError = form.errors[`sections.${index}.title`];

  const handleUploadReady = useCallback(
    (readyFile: UploadableFile) => {
      if (!readyFile.fileData) {
        console.error('readyFile.fileData should not be undefined.');
        return;
      }

      const attachment = {
        video: readyFile.videoData,
        fileId: readyFile.fileData.id,
        type: readyFile.fileData.type,
        name: readyFile.fileData.name,
        previewUrl: readyFile.fileData!.urls?.previewThumbnailUrl,
      };

      form.setFieldValue(
        `sections.${index}.attachments`,
        (currentAttachments: FormAttachment[]) => [...currentAttachments, attachment]
      );
    },
    [form, index]
  );

  const { uploads, startUpload, removeUpload, isLoading } = useFileUploadManager({
    onUploadReady: handleUploadReady,
  });

  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  const handleExistingFilesSelect = async (fileIds: string[]) => {
    const newAttachments = await Promise.all(
      fileIds.map(async fileId => {
        const fileInfo = await fetchFileInfo(fileId);
        return {
          video: fileInfo.video,
          fileId: fileInfo.id,
          type: fileInfo.type,
          name: fileInfo.name,
          previewUrl: fileInfo.urls?.previewThumbnailUrl,
        };
      })
    );
    form.setFieldValue(`sections.${index}.attachments`, (current: typeof newAttachments) => [
      ...current,
      ...newAttachments,
    ]);
  };

  return (
    <div className={styles.section}>
      <div className={styles.leftColumn}>
        <StepIndicator index={index + 1} />

        <div className={styles.verticalLine} />
      </div>
      <div className={styles.inputs}>
        <TextInput
          placeholder={t(
            'instructions.sectionTitle',
            "Titre de l'étape (exemple : Allumer la chaudière)"
          )}
          className={styles.titleInput}
          withAsterisk
          {...form.getInputProps(`sections.${index}.title`)}
          error={!!form.errors[`sections.${index}.title`]}
          errorProps={{
            type: 'default',
            children: null,
          }}
        />
        <RichTextEditor
          value={form.values.sections[index].content}
          onChange={handleEditorChange}
          className={styles.textArea}
          placeholder={t(
            'instructions.sectionContentPlaceholder',
            'Détails de la consigne (exemple : Appuyer sur le bouton sur le côté droit de la chaudière, attendre 3 secondes que le voyant s’allume au vert...)'
          )}
        />
        {titleError && <div className={styles.error}>{titleError}</div>}

        <div className={styles.uploadButtonContainer}>
          <UploadFileButton
            onFilesSelected={files => startUpload(files)}
            onExistingFilesSelect={handleExistingFilesSelect}
            loading={isLoading}
            modalTitle={t(
              'instructions.section.attachments.modalTitle',
              "Ajouter des pièces jointes à l'étape"
            )}
          />
        </div>
        <FormAttachmentViewer
          attachments={form.values.sections[index].attachments}
          uploadingAttachments={uploads}
          onDeleteAttachment={attachment => {
            form.setFieldValue(
              `sections.${index}.attachments`,
              form.values.sections[index].attachments.filter(a => a.fileId !== attachment.fileId)
            );
          }}
          onDeleteUploadingAttachment={localId => removeUpload(localId)}
        />
      </div>
      <div className={styles.rightColumn}>
        <ActionIcon
          radius="xl"
          className={styles.deleteButton}
          onClick={removeSection}
          disabled={form.values.sections.length === 1}
        >
          <IconTrash size={20} />
        </ActionIcon>
      </div>
    </div>
  );
};

export default InstructionFormSection;
